import { ReactNode, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import TaskGroupDrawer, { GroupTaskList } from 'modules/Tasks/Groups/TaskGroupDrawer';
import groupComponentRegistry from 'modules/Tasks/Groups/customComponents/registry';
import useDeepLinkAutoOpen from 'modules/common/hooks/useDeepLinkAutoOpen';
import { GroupActionTypes, TaskGroupSummary } from 'modules/Tasks/types';
import { MEMBER_PRIORITY_TASKS_QUERY_KEY } from 'services/memberTasks/queries/useMemberPriorityTasks';
import { MEMBER_TASK_GROUPS_QUERY_KEY } from 'services/memberTasks/queries/useMemberTaskGroups';

/** Owns the click handler and post-click surface (drawer or custom component) for a group's group_action. */
export default function useGroupAction(
    group: TaskGroupSummary,
    options?: { autoOpen?: boolean }
): {
    onCtaClick: () => void;
    actionSurface: ReactNode;
} {
    const queryClient = useQueryClient();
    const [isOpen, setIsOpen] = useState(false);
    useDeepLinkAutoOpen(Boolean(options?.autoOpen), () => setIsOpen(true));
    // Grouped completions inside the panel deliberately leave the group card + priority feed stale
    // (see useInvalidateMemberTaskQueries) so the card isn't yanked mid-flow. Sync them now that the
    // panel is closing. Unconditional — a no-op refetch when nothing changed.
    const close = () => {
        setIsOpen(false);
        queryClient.invalidateQueries({ queryKey: MEMBER_TASK_GROUPS_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: MEMBER_PRIORITY_TASKS_QUERY_KEY });
    };
    const action = group.group_template.group_action;

    const onCtaClick = () => setIsOpen(true);

    const actionSurface = (() => {
        switch (action.type) {
            case GroupActionTypes.CUSTOM: {
                const Component = groupComponentRegistry[action.component_name];
                return Component ? <Component open={isOpen} onClose={close} group={group} /> : null;
            }
            case GroupActionTypes.EXPAND:
            default:
                return (
                    <TaskGroupDrawer group={group} open={isOpen} onClose={close}>
                        <GroupTaskList group={group} />
                    </TaskGroupDrawer>
                );
        }
    })();

    return { onCtaClick, actionSurface };
}
